import {
  ActionMenu,
  ConfirmationDialog,
  DataGrid,
  FilterToolbar,
  useNotifications,
  type FilterDef,
  type GridQuery
} from '@ijoin/components';
import {
  getGetAdminPlanValidationsQueryKey,
  useDeletePlan,
  useGetAdminPlanValidations,
  type GetAdminPlanValidationsIssueCodesItem,
  type GetAdminPlanValidationsParams,
  type PlanValidationIssueRes
} from '@ijoin/hooks';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';

import { sortBy } from 'lodash';
import { FC, useState } from 'react';

import { LinkButton } from './LinkButton';
import { useAdvisoryOrganizationOptions } from './useAdvisoryOrganizationOptions';
import { usePlanOptions } from './usePlanOptions';

const ISSUE_DESCRIPTIONS: Record<string, string> = {
  enrollmentMissingParticipant:
    'Plan enrollment whose participant record no longer exists.',
  iveMissingEnrollment:
    'Vehicle enrollment whose plan enrollment no longer exists.',
  iveMissingVehicle:
    'Vehicle enrollment whose investment vehicle no longer exists.',
  missingMatchTier:
    'Vehicle match tiers with missing levels or duplicate tiers.',
  noFunds: 'Plan has investment vehicles but no paths with funds.',
  noInvestmentVehicle: 'Plan has no investment vehicle configured.',
  orphanedInvestmentVehicle: 'Investment vehicle whose plan no longer exists.',
  pathMissingFund: 'Vehicle path whose fund no longer exists.',
  pathMissingVehicle: 'Vehicle path whose investment vehicle no longer exists.',
  planMissingMetadata:
    'Plan with no metadata row linking it to an advisory organization.'
};

const ISSUE_LABELS: Record<string, string> = {
  enrollmentMissingParticipant: 'Enrollment missing participant',
  iveMissingEnrollment: 'Vehicle enrollment missing enrollment',
  iveMissingVehicle: 'Vehicle enrollment missing vehicle',
  missingMatchTier: 'Missing match tier(s)',
  noFunds: 'No funds',
  noInvestmentVehicle: 'No investment vehicle',
  orphanedInvestmentVehicle: 'Investment vehicle without a plan',
  pathMissingFund: 'Path missing fund',
  pathMissingVehicle: 'Path missing vehicle',
  planMissingMetadata: 'Plan missing metadata'
};

export const ValidationIssuesTab: FC<{
  issueCodes: GetAdminPlanValidationsIssueCodesItem[];
}> = props => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const [query, setQuery] = useState<GridQuery>();
  const [planToDelete, setPlanToDelete] =
    useState<null | PlanValidationIssueRes>(null);

  const [draftOrgId, setDraftOrgId] = useState<string | undefined>();

  const appliedOrgId =
    draftOrgId ??
    (query as GetAdminPlanValidationsParams | undefined)
      ?.advisoryOrganizationId;

  const orgs = useAdvisoryOrganizationOptions();

  const plans = usePlanOptions(appliedOrgId);

  const validations = useGetAdminPlanValidations(
    { ...query, issueCodes: props.issueCodes } as GetAdminPlanValidationsParams,
    { query: { enabled: !!query, placeholderData: keepPreviousData } }
  );

  const deletePlan = useDeletePlan({
    mutation: {
      onError: () => notifications.error('Failed to delete plan'),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetAdminPlanValidationsQueryKey()
        });
        notifications.success('Plan deletion initiated');
      }
    }
  });

  const onConfirmDelete = () => {
    if (!planToDelete?.planId) return;

    deletePlan.mutate({ planId: planToDelete.planId });
    setPlanToDelete(null);
  };

  const onCancelDelete = () => setPlanToDelete(null);

  const filters: FilterDef[] = [
    {
      isLoading: orgs.isLoading,
      label: 'Advisory Organization',
      name: 'advisoryOrganizationId',
      options: orgs.data ?? [],
      type: 'autocomplete'
    },
    {
      ...plans,
      label: 'Plan',
      name: 'planId',
      type: 'autocomplete'
    },
    {
      label: 'Issue Type',
      name: 'issueCode',
      options: sortBy(
        props.issueCodes.map(code => ({
          label: ISSUE_LABELS[code] ?? code,
          value: code
        })),
        'label'
      ),
      type: 'select'
    }
  ];

  const columns: GridColDef<PlanValidationIssueRes>[] = [
    {
      field: 'advisoryOrgName',
      flex: 1.5,
      headerName: 'Advisory Org',
      renderCell: params =>
        params.row.advisoryOrganizationId ? (
          <LinkButton
            params={{
              advisoryOrganizationId: params.row.advisoryOrganizationId
            }}
            to='/organizations/$advisoryOrganizationId'
            variant='text'>
            {params.value ?? ''}
          </LinkButton>
        ) : (
          (params.value ?? '')
        )
    },
    {
      field: 'planName',
      flex: 1.5,
      headerName: 'Plan',
      renderCell: params =>
        params.row.advisoryOrganizationId && params.row.planId ? (
          <LinkButton
            params={{
              advisoryOrganizationId: params.row.advisoryOrganizationId,
              planId: params.row.planId
            }}
            to='/organizations/$advisoryOrganizationId/plans/$planId'
            variant='text'>
            {params.value ?? ''}
          </LinkButton>
        ) : (
          (params.value ?? '')
        )
    },
    {
      field: 'externalPlanId',
      flex: 1,
      headerName: 'External Plan ID',
      renderCell: params => params.value ?? ''
    },
    {
      field: 'entityId',
      flex: 1.5,
      headerName: 'Record ID',
      renderCell: params => params.value ?? '',
      sortable: false
    },
    {
      field: 'issue',
      flex: 1.25,
      headerName: 'Issue',
      sortable: true,
      valueGetter: (_value, row) => row.issueLabel
    },
    {
      field: 'actions',
      headerName: 'Actions',
      renderCell: params =>
        params.row.planId ? (
          <ActionMenu
            actions={[
              {
                content: 'Delete Plan',
                onSelect: () => setPlanToDelete(params.row)
              }
            ]}
            trigger={
              <IconButton data-testid={`issueActions-${params.row.planId}`}>
                <MoreVertIcon />
              </IconButton>
            }
          />
        ) : null,
      sortable: false,
      width: 80
    }
  ];

  const issueInfo = (
    <Tooltip
      title={
        <Stack>
          {sortBy(props.issueCodes).map(code => (
            <Typography key={code}>
              <b>{ISSUE_LABELS[code] ?? code}:</b> {ISSUE_DESCRIPTIONS[code]}
            </Typography>
          ))}
        </Stack>
      }>
      <IconButton color='inherit'>
        <InfoOutlinedIcon data-testid='issueTypesInfo' />
      </IconButton>
    </Tooltip>
  );

  return (
    <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        filterDefs={filters}
        getRowId={row => `${row.issueCode}:${row.entityId ?? row.planId}`}
        initialSort={[{ field: 'advisoryOrgName', sort: 'asc' }]}
        loading={!query || validations.isFetching}
        onClear={setQuery}
        onSearch={setQuery}
        pageSize={25}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationMode='server'
        rowCount={validations.data?.pagination?.total ?? 0}
        rows={validations.data?.data ?? []}
        showColumnMenu
        showToolbar
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'planValidations'
          },
          toolbar: {
            actions: issueInfo,
            onDraftChange: (values: Record<string, unknown>) =>
              setDraftOrgId(
                (values.advisoryOrganizationId as string) || undefined
              ),
            onRefresh: validations.refetch,
            searchButtonTestId: 'search',
            triggerTestId: 'validationsFilterTrigger'
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sortingMode='server'
        sx={{ flex: 1, minHeight: 0 }}
      />
      <ConfirmationDialog
        confirmText='Delete'
        data-testid='deletePlanFromIssues'
        isOpen={!!planToDelete}
        message={`This will permanently delete "${planToDelete?.planName ?? planToDelete?.planId}" and all of its data.`}
        onCancel={onCancelDelete}
        onConfirm={onConfirmDelete}
        title='Delete Plan'
      />
    </Stack>
  );
};

ValidationIssuesTab.displayName = 'ValidationIssuesTab';
